/**
 * Pan Smoothing
 *
 * Factory function for smoothing stereo panning values to prevent audio jitter.
 */

export interface PanSmootherOptions {
  smoothingFactor?: number;
  changeThreshold?: number;
  centerDeadZone?: number;
}

export const DEFAULT_PAN_SMOOTHER_OPTIONS: Required<PanSmootherOptions> = {
  smoothingFactor: 0.3,
  changeThreshold: 0.02,
  centerDeadZone: 0.03,
};

// ─── Factory function (preferred) ────────────────────────────────────────────

export interface PanSmootherHandle {
  smooth(participantId: string, newPanValue: number): number;
  clear(participantId: string): void;
  clearAll(): void;
}

export function createPanSmoother(
  options: PanSmootherOptions = {}
): PanSmootherHandle {
  const opts = {...DEFAULT_PAN_SMOOTHER_OPTIONS, ...options};
  const smoothedValues = new Map<string, number>();
  const lastUpdateTime = new Map<string, number>();

  function smooth(participantId: string, newPanValue: number): number {
    const previousPan = smoothedValues.get(participantId);
    const now = Date.now();
    const lastTime = lastUpdateTime.get(participantId) || 0;
    const timeSinceLastUpdate = now - lastTime;

    let targetPan = newPanValue;
    if (Math.abs(newPanValue) < opts.centerDeadZone) {
      targetPan = 0;
    }

    if (previousPan === undefined) {
      smoothedValues.set(participantId, targetPan);
      lastUpdateTime.set(participantId, now);
      return targetPan;
    }

    const panChange = Math.abs(targetPan - previousPan);

    if (panChange < opts.changeThreshold) {
      return previousPan;
    }

    let effectiveSmoothingFactor = opts.smoothingFactor;

    const signFlipped =
      (previousPan > 0 && targetPan < 0) || (previousPan < 0 && targetPan > 0);
    const bothNearCenter =
      Math.abs(previousPan) < 0.15 && Math.abs(targetPan) < 0.15;

    const isLikelyJitter =
      signFlipped && panChange > 1.0 && timeSinceLastUpdate < 60;

    if (isLikelyJitter) {
      effectiveSmoothingFactor = 0.7;
    } else if (bothNearCenter) {
      effectiveSmoothingFactor = Math.min(opts.smoothingFactor + 0.1, 0.5);
    }

    const smoothedPan =
      previousPan * effectiveSmoothingFactor +
      targetPan * (1 - effectiveSmoothingFactor);

    const finalPan =
      Math.abs(smoothedPan) < opts.centerDeadZone ? 0 : smoothedPan;

    smoothedValues.set(participantId, finalPan);
    lastUpdateTime.set(participantId, now);

    return finalPan;
  }

  function clear(participantId: string): void {
    smoothedValues.delete(participantId);
    lastUpdateTime.delete(participantId);
  }

  function clearAll(): void {
    smoothedValues.clear();
    lastUpdateTime.clear();
  }

  return {smooth, clear, clearAll};
}
